import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { createClient } from '@supabase/supabase-js';

function Report() {
    const debug = false;
    const [fetchingReport, setFetchingReport] = useState(true);
    const settingSavedCountdown = 2;
    const params = useParams();
    const [settingsSaved, setSettingsSaved] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([1]);
    const optionNameMap = ['Refinance calculator', 'Monthly mortgage cost estimator', 'Equity tracker', 'Home affordability tool', 'Property tax estimator'];
    const options = [
        {
            id: 0,
            name: optionNameMap[0],
            description: "How much money could I save by refinancing?",
        },
        {
            id: 1,
            name: optionNameMap[1],
            description: "Understand your payments based on current home value and loan terms.",
        },
        {
            id: 2,
            name: optionNameMap[2],
            description: "View how much equity you've gained since purchase.",
        },
        {
            id: 3,
            name: optionNameMap[3],
            description: "Expolore what you can afford if you sell and buy again.",
        },
        {
            id: 4,
            name: optionNameMap[4],
            description: 'Get a quick estimate of your current property tax burden.'
        }
    ];
    const [report, setReport] = useState({
        id: '36',
        guid: 'cb38f00d-02af-446b-ba5f-5e400cd59798',
        user_id: '44',
        property_id: '34',
        created_at: '2025-07-28 19:19:10.964039+00',
        data: {
            "place_id": "ChIJj2UBJZrd9YgR9yWJ7L-eetA",
            "estimates": [
              // Current month estimates (July 2025)
              {
                "url": "https://www.realtor.com/myhome/262-Woodland-Dr_Winder_GA_30680_M69712-07193?claimSource=myhome_landing_primary_search_bar",
                "date": "2025-07-22",
                "estimate": 660369,
                "service_id": 1,
                "service_name": "Realtor.com"
              },
              {
                "url": "https://www.zillow.com/homedetails/262-Woodland-Dr-Winder-GA-30680/54550296_zpid/",
                "date": "2025-07-22",
                "estimate": 657300,
                "service_id": 2,
                "service_name": "Zillow"
              },
              {
                "url": "https://www.redfin.com/GA/Winder/262-Woodland-Dr-30680/home/23536632",
                "date": "2025-07-22",
                "estimate": 637197,
                "service_id": 3,
                "service_name": "Redfin"
              },
              // Previous month estimates (June 2025)
              {
                "url": "https://www.realtor.com/myhome/262-Woodland-Dr_Winder_GA_30680_M69712-06193?claimSource=myhome_landing_primary_search_bar",
                "date": "2025-06-15",
                "estimate": 645000,
                "service_id": 1,
                "service_name": "Realtor.com"
              },
              {
                "url": "https://www.zillow.com/homedetails/262-Woodland-Dr-Winder-GA-30680/54550296_zpid/",
                "date": "2025-06-15",
                "estimate": 642000,
                "service_id": 2,
                "service_name": "Zillow"
              },
              {
                "url": "https://www.redfin.com/GA/Winder/262-Woodland-Dr-30680/home/23536632",
                "date": "2025-06-15",
                "estimate": 625000,
                "service_id": 3,
                "service_name": "Redfin"
              }
            ],
            "formatted_address": "262 Woodland Dr, Winder, GA 30680, USA"
          }
    });

    // Utility functions for working with estimates
    const groupEstimatesByMonth = useMemo(() => {
        const grouped = {};
        report.data.estimates.forEach(estimate => {
            const date = new Date(estimate.date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            if (!grouped[monthKey]) {
                grouped[monthKey] = [];
            }
            grouped[monthKey].push(estimate);
        });
        return grouped;
    }, [report.data.estimates]);

    const getMonthKey = (date) => {
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };

    const getNextMonthKey = () => {
        const reportDate = new Date(report.created_at);
        const nextMonth = new Date(reportDate.getFullYear(), reportDate.getMonth() + 1, 1);
        return getMonthKey(nextMonth);
    };

    const curMonthEstimate = useMemo(() => {
        const reportDate = new Date(report.created_at);
        const currentMonthKey = `${reportDate.getFullYear()}-${String(reportDate.getMonth() + 1).padStart(2, '0')}`;
        const currentMonthEstimates = groupEstimatesByMonth[currentMonthKey] || [];
        
        if (currentMonthEstimates.length === 0) {
            return null;
        }

        // Calculate average estimate for current month
        const totalEstimate = currentMonthEstimates.reduce((sum, est) => sum + est.estimate, 0);
        const averageEstimate = Math.round(totalEstimate / currentMonthEstimates.length);

        return {
            month: currentMonthKey,
            estimates: currentMonthEstimates,
            averageEstimate,
            count: currentMonthEstimates.length
        };
    }, [groupEstimatesByMonth, report.created_at]);

    const prevMonthEstimate = useMemo(() => {
        const reportDate = new Date(report.created_at);
        const prevMonth = new Date(reportDate.getFullYear(), reportDate.getMonth() - 1, 1);
        const previousMonthKey = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`;
        const previousMonthEstimates = groupEstimatesByMonth[previousMonthKey] || [];
        
        if (previousMonthEstimates.length === 0) {
            return null;
        }

        // Calculate average estimate for previous month
        const totalEstimate = previousMonthEstimates.reduce((sum, est) => sum + est.estimate, 0);
        const averageEstimate = Math.round(totalEstimate / previousMonthEstimates.length);

        return {
            month: previousMonthKey,
            estimates: previousMonthEstimates,
            averageEstimate,
            count: previousMonthEstimates.length
        };
    }, [groupEstimatesByMonth, report.created_at]);

    // Calculate month-over-month change
    const monthOverMonthChange = useMemo(() => {
        if (!curMonthEstimate || !prevMonthEstimate) {
            return null;
        }

        const change = curMonthEstimate.averageEstimate - prevMonthEstimate.averageEstimate;
        const percentChange = (change / prevMonthEstimate.averageEstimate) * 100;

        return {
            absolute: change,
            percent: Math.round(percentChange * 100) / 100, // Round to 2 decimal places
            isPositive: change > 0
        };
    }, [curMonthEstimate, prevMonthEstimate]);

    // Utility function to get all available months
    const getAllAvailableMonths = useMemo(() => {
        return Object.keys(groupEstimatesByMonth).sort();
    }, [groupEstimatesByMonth]);

    // Debug function to log grouped data
    const logGroupedData = () => {
        console.log('Grouped Estimates by Month:', groupEstimatesByMonth);
        console.log('Current Month Estimate:', curMonthEstimate);
        console.log('Previous Month Estimate:', prevMonthEstimate);
        console.log('Month-over-Month Change:', monthOverMonthChange);
        console.log('All Available Months:', getAllAvailableMonths);
    };

    const handleOptionClick = (optionId) => {
        if (selectedOptions.includes(optionId)) {
            setSelectedOptions(selectedOptions.filter(o => o !== optionId));
        } else {
            setSelectedOptions([...selectedOptions, optionId]);
        }
    }

    const handleSaveSettings = async () => {
        setIsSubmitting(true);
        const payload = [{
            report_id: report.id,
            options: selectedOptions.map(optionId => optionNameMap[optionId])
        }];
        console.log('Payload:', payload);
        const response = await fetch('https://movebuddha.app.n8n.cloud/webhook/9a75c06f-09d9-40d1-83d0-19ec181b98b3', {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        setIsSubmitting(false);
        setSettingsSaved(true);
        setTimeout(() => {
            setSettingsSaved(false);
        }, settingSavedCountdown * 1000);
        return response.json();
    }

    useEffect(() => {
        if (debug) {
            return;
        }
        const supabaseUrl = 'https://dtfzqblidhxerysbvpnf.supabase.co';
        const supabaseKey = 'sb_publishable_7TGOGQZbi47oKkUAk1kv7g_nZU5dw3_';
        const supabase = createClient(supabaseUrl, supabaseKey);
        const fetchReport = async () => {
        const { data, error } = await supabase
            .from('user_property_reports')
            .select('*')
            .eq('guid', params.guid)
            .single();
        if (error) {
            console.error('Error fetching report:', error);
        } else {
            setReport(data);
        }
    }
        fetchReport();
    }, [params.guid, debug]);

    const loadingSteps = useMemo(() => ['Getting your address...', 'Searching for property values...', 'Combining results...', 'Generating report...'], []);
    const [loadingStep, setLoadingStep] = useState(0);
    useEffect(() => {
        if (fetchingReport) {
            const randomDelay = Math.floor(Math.random() * 5000-3000+1) + 3000;
            const interval = setInterval(() => {
                if (loadingStep < loadingSteps.length - 1) {    
                    setLoadingStep(loadingStep + 1);
                } else {
                    setFetchingReport(false);
                }
            }, randomDelay);
            return () => clearInterval(interval);
        }
    }, [fetchingReport, loadingStep, loadingSteps.length]);


  return (
    <>
    {fetchingReport && (
        <div className="p-4 flex justify-center items-center absolute top-0 left-0 backdrop-blur-lg bg-black/30 w-full h-full">
            <div className="flex justify-center items-center">
                <div className="bg-gray-600 p-8 rounded-md text-center">
                    <h1 className="text-gray-100 mb-2 text-xl"><i className="far fa-wave-pulse mr-1 text-teal-400"></i> Home<span className="text-teal-400">Pulse</span></h1>
                    <div className="text-gray-300">
                    <i className="far fa-spinner fa-spin mr-2"></i>
                    {loadingSteps[loadingStep]}
                    </div>
                </div>
            </div>
        </div>
    )}
    <div className="bg-gray-950 text-gray-200 p-4">
        <div className="flex justify-between items-center max-w-screen-xl mx-auto">
        <div className="font-bold text-xl w-1/2">
            <i className="far fa-wave-pulse mr-2 text-teal-600"></i>
            Home<span className="text-teal-600">Pulse</span>
        </div>
        <div className="text-right w-1/2">
            <div>
                {new Date(report.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Report
            </div>
            <div className="text-right text-xs text-gray-400">
                {report.data.formatted_address}
            </div>
        </div>
        </div>
    </div>
    <div className="bg-gray-900 text-gray-100 w-full h-full ">
        <div className="max-w-screen-2xl mx-auto p-12">
            <div className="text-center mb-8">
                <div className="text-xl text-gray-300 mb-2">
                    The estimated value of your&nbsp;home&nbsp;is
                </div>
                <div className="text-4xl text-teal-400 mb-6 font-thin tracking-wide">
                    $ {curMonthEstimate ? curMonthEstimate.averageEstimate.toLocaleString() : 
                       Math.round(report.data.estimates.reduce((acc, curr) => acc + curr.estimate, 0) / report.data.estimates.length).toLocaleString()}
                </div>
                 
                {/* Month-over-month change indicator */}
                {monthOverMonthChange && (
                    <div className={`text-lg mb-4 ${monthOverMonthChange.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                        <span className="bg-gray-700 rounded-full p-2 px-4">{monthOverMonthChange.isPositive ? '+' : '-'}{monthOverMonthChange.percent}%</span>
                    </div>
                )}
            </div>
            
            <div className="flex flex-col md:flex-row gap-6 items-center justify-center">
                {curMonthEstimate ? 
                    curMonthEstimate.estimates.map((estimate) => (
                        <div key={estimate.service_id} className="flex flex-col items-center justify-center bg-gray-700 rounded-md p-6 py-8 w-full md:w-1/3">
                            <div className="text-2xl text-gray-100 font-thin mb-2 tracking-wide">
                                <span className="text-gray-400">$</span> {estimate.estimate.toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-200">{estimate.service_name}</div>
                            <a href={estimate.url} target="_blank" className="text-xs text-teal-400" rel="noreferrer">
                                visit site <i className="ml-1 fal fa-arrow-up-right-from-square"/>
                            </a>
                        </div>
                    ))
                    :
                    report.data.estimates.map((estimate) => (
                        <div key={estimate.service_id} className="flex flex-col items-center justify-center bg-gray-700 rounded-md p-6 py-8 w-full md:w-1/3">
                            <div className="text-2xl text-gray-100 font-thin mb-2 tracking-wide">
                                <span className="text-gray-400">$</span> {estimate.estimate.toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-200">{estimate.service_name}</div>
                            <a href={estimate.url} target="_blank" className="text-xs text-teal-400" rel="noreferrer">
                                visit site <i className="ml-1 fal fa-arrow-up-right-from-square"/>
                            </a>
                        </div>
                    ))
                }
            </div>
            
            {/* Previous month comparison */}
            {prevMonthEstimate && (
                <div className="mt-8 p-6 bg-gray-800 rounded-md">
                    <h3 className="text-lg text-gray-200 mb-4 text-center">
                        Previous Month Comparison
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="text-center">
                            <div className="text-sm text-gray-400 mb-2">Previous Month Average</div>
                            <div className="text-2xl text-gray-300">
                                ${prevMonthEstimate.averageEstimate.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {new Date(prevMonthEstimate.month + '-01 00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="text-sm text-gray-400 mb-2">Month-over-Month Change</div>
                            <div className={`text-2xl ${monthOverMonthChange?.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                                {monthOverMonthChange?.isPositive ? '+' : ''}${Math.abs(monthOverMonthChange?.absolute || 0).toLocaleString()}
                            </div>
                            <div className={`text-sm ${monthOverMonthChange?.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                                {monthOverMonthChange?.isPositive ? '+' : ''}{monthOverMonthChange?.percent || 0}%
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Debug Section - Remove in production */}
            {debug && (
            <div className="mt-8 p-6 bg-gray-800 rounded-md">
                <h3 className="text-lg text-gray-200 mb-4 text-center">
                    Debug Information
                </h3>
                <div className="space-y-4 text-sm">
                    <div>
                        <div className="text-gray-400 mb-2">Available Months:</div>
                        <div className="text-gray-300">
                            {getAllAvailableMonths.map(month => (
                                <span key={month} className="inline-block bg-gray-700 px-2 py-1 rounded mr-2 mb-2">
                                    {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                </span>
                            ))}
                        </div>
                    </div>
                    
                    <div>
                        <div className="text-gray-400 mb-2">Current Month Data:</div>
                        <div className="text-gray-300">
                            {curMonthEstimate ? (
                                <div>
                                    <div>Month: {curMonthEstimate.month}</div>
                                    <div>Average: ${curMonthEstimate.averageEstimate.toLocaleString()}</div>
                                    <div>Count: {curMonthEstimate.count} estimates</div>
                                </div>
                            ) : (
                                <div className="text-red-400">No current month data available</div>
                            )}
                        </div>
                    </div>
                    
                    <div>
                        <div className="text-gray-400 mb-2">Previous Month Data:</div>
                        <div className="text-gray-300">
                            {prevMonthEstimate ? (
                                <div>
                                    <div>Month: {prevMonthEstimate.month}</div>
                                    <div>Average: ${prevMonthEstimate.averageEstimate.toLocaleString()}</div>
                                    <div>Count: {prevMonthEstimate.count} estimates</div>
                                </div>
                            ) : (
                                <div className="text-red-400">No previous month data available</div>
                            )}
                        </div>
                    </div>
                    
                    <button 
                        onClick={logGroupedData}
                        className="bg-teal-600 text-white px-4 py-2 rounded text-xs"
                    >
                        Log Data to Console
                    </button>
                </div>
            </div>
            )}
        </div>
        <div className="bg-gray-800">
        <div className="text-gray-300 p-8 rounded-md max-w-screen-xl mx-auto py-12">
            <div className="text-xl text-center mb-8">
                What do you want to see in your <span className="text-teal-400">{new Date(getNextMonthKey() + '-01 00:00:00').toLocaleDateString('en-US', { month: 'long' })}</span> home&nbsp;pulse&nbsp;report?
            </div>
            <div className="flex flex-col gap-6 mb-8">
                {options.map((option) => (
                    <div className={`text-gray-300 flex bg-gray-700 rounded-md p-6 cursor-pointer ${selectedOptions.includes(option.id) ? '' : ''}`} onClick={() => handleOptionClick(option.id)}>
                        <div className="">
                            <i className={`text-2xl mr-4 ${selectedOptions.includes(option.id) ? 'fas text-teal-400 fa-square-check' : 'fal fa-square text-gray-400'}`}/>
                        </div>
                        <div className="">
                        <div className="text-lg text-teal-400 leading-tight mb-1">{option.name}</div>
                        <div className="text-sm text-gray-300">{option.description}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-center">
                {settingsSaved ? (
                    <div className="text-teal-400 px-4 py-2 rounded-md">Settings saved!</div>
                ) : (
                    <button disabled={isSubmitting} className="bg-teal-400 text-gray-900 px-4 py-2 rounded-md" onClick={handleSaveSettings}><i className={`fal mr-2 inline-block mx-auto ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-save'}`} ></i>{isSubmitting ? 'Saving settings...' : 'Save settings'}</button>
                )}
            </div>
        </div>
        </div>
    </div>
    </>
  );
}

export default Report;